import { RefObject, useCallback, useEffect, useRef } from "react";
import { useAppStore } from "../../hooks/useStores.tsx";
import { IBrushSettings } from "../../models/brush.ts";
import { SegmentationMaskTypes } from "../../pages/Playground/ImageToImage/Config/Features/EraseObject/eraseObjectConstants.ts";

export const useBrushCore = (
	brushConfig: IBrushSettings,
	canvasRef: RefObject<HTMLCanvasElement | null>,
	brushingAreaCanvasRef: RefObject<HTMLCanvasElement | null>,
	cursorRef: RefObject<HTMLDivElement | null>,
) => {
	const pathRef = useRef<Path2D | null>(null);
	const isDrawingRef = useRef(false);
	const previousCoordinatesRef = useRef([0, 0]);
	const overlayContextRef = useRef<CanvasRenderingContext2D | null>(null);
	const capturedMaskRef = useRef<string | null>(null);
	const updateCoordinates = useCallback((e: MouseEvent) => {
		previousCoordinatesRef.current = [e.offsetX, e.offsetY];
	}, []);
	const { imageToImageStore } = useAppStore();
	const { editingCanvasStore } = imageToImageStore;

	const drawLine = useCallback(
		(e: MouseEvent) => {
			if (!overlayContextRef.current || !pathRef.current || !isDrawingRef.current) {
				return;
			}
			const yValue = e.offsetY;
			const xValue = e.offsetX;

			updateCoordinates(e);
			pathRef.current.lineTo(xValue, yValue);
			overlayContextRef.current.stroke(pathRef.current);
		},
		[updateCoordinates, brushConfig],
	);

	const drawEnd = useCallback(() => {
		isDrawingRef.current = false;
		pathRef.current = null;
		canvasRef.current?.removeEventListener("mousemove", drawLine);
		canvasRef.current?.removeEventListener("mouseup", drawEnd);
	}, [drawLine, brushConfig, canvasRef]);

	const drawStart = useCallback(
		(e: MouseEvent) => {
			if (editingCanvasStore.objectManipulationConfig?.mask_type === SegmentationMaskTypes.AUTOMATIC) return;
			if (["point", "point-erase"].includes(brushConfig.brushMode)) return;

			isDrawingRef.current = true;
			updateCoordinates(e);
			pathRef.current = new Path2D();
			pathRef.current.moveTo(previousCoordinatesRef.current[0], previousCoordinatesRef.current[1]);
			canvasRef.current?.addEventListener("mousemove", drawLine);
			canvasRef.current?.addEventListener("mouseup", drawEnd);
			if (overlayContextRef.current && brushConfig) {
				overlayContextRef.current.strokeStyle = brushConfig.strokeStyle;
				overlayContextRef.current.lineWidth = brushConfig.lineWidth;
				overlayContextRef.current.lineCap = brushConfig.lineCap;
				overlayContextRef.current.lineJoin = brushConfig.lineJoin;
				overlayContextRef.current.globalAlpha = brushConfig.globalAlpha;
				overlayContextRef.current.globalCompositeOperation = brushConfig.globalCompositeOperation;
			}
			editingCanvasStore.toggleIsMaskApplied(true);
			return () => {
				if (canvasRef.current) {
					canvasRef.current.removeEventListener("mousemove", drawLine);
					canvasRef.current.removeEventListener("mouseup", drawEnd);
				}
			};
		},
		[drawEnd, drawLine, brushConfig, overlayContextRef.current],
	);

	const drawCursor = useCallback(
		(e: MouseEvent) => {
			if (["point", "point-erase"].includes(brushConfig.brushMode)) return;

			const cursor = cursorRef.current;
			if (canvasRef && canvasRef.current && cursor) {
				canvasRef.current.style.cursor = "none";
				cursor.style.display = "block";
				cursor.style.transform = `translate3d(${e.offsetX - (canvasRef.current?.width ?? 0) / 2}px, ${
					e.offsetY - brushConfig.lineWidth / 2
				}px, 0)`;
			}
		},
		[brushConfig, canvasRef, cursorRef],
	);

	const handleMouseLeave = useCallback(() => {
		canvasRef.current?.removeEventListener("mousemove", drawCursor);
		const cursor = cursorRef.current;
		if (canvasRef.current && cursor) {
			cursor.style.display = "none";
			canvasRef.current.style.cursor = "default";
		}
	}, [drawCursor, canvasRef, cursorRef]);

	const handleMouseEnter = useCallback(
		(e: MouseEvent) => {
			canvasRef.current?.addEventListener("mousemove", drawCursor);

			// If left mouse button is pressed (buttons === 1), start drawing
			e.buttons === 1 && drawStart(e);
		},
		[drawCursor, canvasRef, drawStart],
	);

	useEffect(() => {
		if (canvasRef.current) {
			canvasRef.current.addEventListener("mousedown", drawStart);
			canvasRef.current.addEventListener("mouseenter", handleMouseEnter);
			canvasRef.current.addEventListener("mouseleave", handleMouseLeave);
			canvasRef.current.addEventListener("mouseleave", drawEnd);
		}

		return () => {
			if (canvasRef.current) {
				canvasRef.current.removeEventListener("mousedown", drawStart);
				canvasRef.current.removeEventListener("mouseenter", handleMouseEnter);
				canvasRef.current.removeEventListener("mouseleave", handleMouseLeave);
				canvasRef.current.removeEventListener("mouseleave", drawEnd);
				canvasRef.current.removeEventListener("mousemove", drawCursor);
			}
		};
	}, [canvasRef, brushConfig, drawStart, handleMouseEnter, handleMouseLeave, drawEnd, drawCursor]);

	useEffect(() => {
		if (brushingAreaCanvasRef && brushingAreaCanvasRef.current && canvasRef.current) {
			brushingAreaCanvasRef.current.style.position = "absolute";
			brushingAreaCanvasRef.current.style.pointerEvents = "none";
			brushingAreaCanvasRef.current.style.width = canvasRef.current.style.width;
			brushingAreaCanvasRef.current.style.height = canvasRef.current.style.height;
			brushingAreaCanvasRef.current.style.opacity = String(brushConfig.brushColorOpacity);
			overlayContextRef.current = brushingAreaCanvasRef.current.getContext("2d");
		}
	}, [canvasRef]);

	const captureMask = useCallback(() => {
		const overlay = brushingAreaCanvasRef.current;
		if (overlay && overlay.width > 0 && overlay.height > 0) {
			capturedMaskRef.current = overlay.toDataURL("image/png");
		}
	}, [brushingAreaCanvasRef]);

	const restoreMask = useCallback(() => {
		const capturedMask = capturedMaskRef.current;
		const overlay = brushingAreaCanvasRef.current;

		if (!capturedMask || !overlay) {
			return;
		}

		const ctx = overlay.getContext("2d");
		if (!ctx) {
			return;
		}

		const img = new Image();
		img.src = capturedMask;
		img.onload = () => {
			// Use smooth scaling for better edge quality
			ctx.imageSmoothingEnabled = true;
			ctx.imageSmoothingQuality = "high";
			ctx.drawImage(img, 0, 0, overlay.width, overlay.height);

			// Apply alpha threshold to sharpen edges while keeping smooth curves
			const imageData = ctx.getImageData(0, 0, overlay.width, overlay.height);
			const pixels = imageData.data;
			const alphaThreshold = 128;

			for (let i = 3; i < pixels.length; i += 4) {
				// Threshold the alpha channel: above threshold = fully opaque, below = transparent
				pixels[i] = pixels[i] > alphaThreshold ? 255 : 0;
			}

			ctx.putImageData(imageData, 0, 0);
			capturedMaskRef.current = null;
		};
	}, [brushingAreaCanvasRef]);

	return {
		captureMask,
		restoreMask,
	};
};
